import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Play, X, Sparkles, Zap, Lightbulb } from "lucide-react";

// Short strategic hints per holdable card. Keyed by card id so the tip
// stays in sync with the card registry in /app/backend/app/data/card_registry.py.
const STRATEGY_TIPS = {
  action_double_investment:
    "Save it for your most expensive draw - the -$50 applies to any buy, so it's best on high-value crypto.",
  action_price_freeze:
    "Play right before drawing your effect card (or when an opponent is likely to target your top holding). Note: Market Melees ignore the freeze.",
  action_insider_information:
    "Best used the turn before your next draw - peek ahead to line up a banger or dodge a Market Melee.",
  action_hedge_bet:
    "Activate on your rarest or highest-value crypto when the event discard pile is heating up - it shields only against Events, not Melees.",
};

/**
 * Confirmation modal shown when a player clicks a holdable card in their
 * hand. Surfaces the card's full description and effect, with "Play Card"
 * and "Cancel" CTAs. Playing a card consumes the turn's play-action budget
 * and disables the Draw Effect step (backend enforces via `played_held_this_turn`).
 *
 * Props:
 *   card        — the EffectCard object (null to hide the modal)
 *   onPlay()    — user confirmed, execute the action
 *   onCancel()  — user dismissed
 *   loading     — true while the play action is in flight
 */
export default function EffectCardDetailModal({ card, onPlay, onCancel, loading = false }) {
  const { t } = useTranslation();
  const open = !!card;
  const subtypeLabel = card?.subtype === "market_melee"
    ? t("effectCardModal.subtype.marketMelee")
    : card?.subtype === "event"
    ? t("effectCardModal.subtype.event")
    : t("effectCardModal.subtype.action");
  const subtypeTone =
    card?.subtype === "event"
      ? "from-orange-500 to-orange-700"
      : card?.subtype === "market_melee"
      ? "from-red-500 to-rose-600"
      : "from-blue-500 to-indigo-600";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onCancel(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-3xl" data-testid="play-card-modal">
        {/* Top accent bar */}
        <div className={`bg-gradient-to-r ${subtypeTone} px-5 py-3 flex items-center gap-2 text-white`}>
          {card?.subtype === "market_melee" ? (
            <Zap className="w-4 h-4" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">{subtypeLabel}</span>
        </div>

        <div className="px-6 py-5">
          <DialogHeader className="space-y-2">
            <DialogTitle
              className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD] leading-tight"
              style={{ fontFamily: "Fredoka" }}
              data-testid="play-card-modal-name"
            >
              {card?.name || ""}
            </DialogTitle>
            {card?.resolutionType && (
              <span
                className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                  card.resolutionType === "holdable"
                    ? "bg-blue-100 text-blue-700"
                    : card.resolutionType === "persistent"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {card.resolutionType}
              </span>
            )}
          </DialogHeader>

          <p className="mt-4 text-sm text-[#1A237E]/75 dark:text-[#E3F2FD]/75 leading-relaxed" data-testid="play-card-modal-description">
            {card?.description || ""}
          </p>

          {/* Strategy tip — gentle guidance on WHEN to play the card */}
          {card?.id && STRATEGY_TIPS[card.id] && (
            <div
              className="mt-3 rounded-xl bg-[#38B6FF]/10 border border-[#38B6FF]/25 px-3 py-2 flex gap-2"
              data-testid="play-card-modal-tip-strategy"
            >
              <Lightbulb className="w-4 h-4 text-[#38B6FF] shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#1A237E]/80 dark:text-[#E3F2FD]/80 leading-relaxed">
                <strong className="font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("effectCardModal.tip.label")}</strong>{" "}
                {STRATEGY_TIPS[card.id]}
              </p>
            </div>
          )}

          <div
            className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800"
            data-testid="play-card-modal-tip"
          >
            <strong className="font-bold">{t("effectCardModal.tip.headsUp")}</strong> {t("effectCardModal.tip.headsUpBody")}
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="rounded-full px-5"
            onClick={onCancel}
            disabled={loading}
            data-testid="play-card-cancel-btn"
          >
            <X className="w-4 h-4 mr-1" /> {t("common.cancel")}
          </Button>
          <Button
            className="rounded-full px-5 bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold"
            onClick={onPlay}
            disabled={loading}
            data-testid="play-card-confirm-btn"
          >
            <Play className="w-4 h-4 mr-1" /> {loading ? t("effectCardModal.button.playing") : t("effectCardModal.button.play")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
